import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

export default function Services() {
  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "IT Services for Businesses",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Perfect Signs Canada",
      "telephone": "+1-780-554-3949",
      "email": "info@perfectsignscanada.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "6109 176 Ave NW",
        "addressLocality": "Edmonton",
        "addressRegion": "AB",
        "postalCode": "T5Y 0K8",
        "addressCountry": "CA"
      }
    },
    "areaServed": "Canada",
    "serviceType": [
      "Website design",
      "Web application development",
      "SEO optimization",
      "Hosting",
      "POS systems",
      "Online ordering",
      "Database design",
      "Email integration"
    ],
    "url": "https://perfectsignscanada.com/services.html",
    "description": "Website, SEO, hosting, POS, online ordering, database, chatbot, and business technology services for Canadian businesses."
  };

  return (
    <>
      <SEO 
        title="IT Services Canada | Websites, SEO, Hosting & POS | Perfect Signs"
        description="Perfect Signs Canada provides website design, web applications, SEO, hosting, SSL, email integration, online ordering, chatbots, databases, and POS systems for businesses."
        keywords="IT services Edmonton, website design Canada, web applications, SEO services, hosting services, SSL certificate, POS systems, online ordering, chatbot, database design, business email integration"
        canonical="https://perfectsignscanada.com/services.html"
        schema={servicesSchema}
      />

      {/* ======= Breadcrumbs ======= */}
      <div className="breadcrumbs d-flex align-items-center" style={{ backgroundImage: "url('/assets/img/services-header.jpg')" }}>
        <div className="container position-relative d-flex flex-column align-items-center" data-aos="fade-up">
          <p className="breadcrumbs-eyebrow">IT Services</p>
          <h2>Websites, systems, and digital tools that make business easier to run.</h2>
          <p className="breadcrumbs-summary">From professional websites and SEO to hosting, POS systems, online ordering, databases, and email integrations, we build practical technology around real business needs.</p>
          <ol>
            <li><Link to="/">Home</Link></li>
            <li>IT Services</li>
          </ol>
        </div>
      </div>
      {/* End Breadcrumbs */}

      {/* ======= IT Intro Section ======= */}
      <section className="it-intro">
        <div className="container" data-aos="fade-up">
          <div className="row gy-4 align-items-center">
            <div className="col-lg-7" data-aos="fade-up" data-aos-delay="100">
              <p className="section-kicker">Digital support with a practical mindset</p>
              <h2>Technology should support your work, not slow it down.</h2>
              <p>Perfect Signs Canada helps businesses set up the digital pieces that matter most: a fast website, reliable hosting, secure forms, online ordering, clear SEO foundations, and business systems that are easy for your team to use.</p>
            </div>
            <div className="col-lg-5" data-aos="fade-up" data-aos-delay="250">
              <div className="it-intro-panel">
                <div>
                  <strong>Plan</strong>
                  <span>Clarify goals, users, workflow, content, and budget.</span>
                </div>
                <div>
                  <strong>Build</strong>
                  <span>Create clean websites, integrations, and systems with practical tools.</span>
                </div>
                <div>
                  <strong>Support</strong>
                  <span>Help with hosting, updates, email, SEO, and ongoing improvements.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* End IT Intro Section */}

      {/* ======= Services Cards Section ======= */}
      <section id="services-cards" className="services-cards it-services-grid">
        <div className="container" data-aos="fade-up">
          <div className="section-header" data-aos="fade-up">
            <h2>IT Services</h2>
            <p>Choose one service or combine several into a complete digital foundation for your business.</p>
          </div>

          <div className="row gy-4">
            {/* Website Design */}
            <div className="col-lg-6" data-aos="fade-up" data-aos-delay="100">
              <div className="card-item">
                <div className="row">
                  <div className="col-xl-5">
                    <div className="card-bg" style={{ backgroundImage: "url('/assets/img/it/WebsiteDesigning.jpg')" }}></div>
                  </div>
                  <div className="col-xl-7 d-flex align-items-center">
                    <div className="card-body">
                      <h4 className="card-title">Website Design</h4>
                      <p>At Perfect Signs, we specialize in crafting visually stunning and highly functional websites. Whether you're launching a new online presence or looking to revamp your current site, our team of experienced designers and developers will work closely with you to create a website that perfectly aligns with your brand and objectives. From responsive design for mobile users to user-friendly navigation, we ensure your website leaves a lasting impression.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Web Applications */}
            <div className="col-lg-6" data-aos="fade-up" data-aos-delay="200">
              <div className="card-item">
                <div className="row">
                  <div className="col-xl-5">
                    <div className="card-bg" style={{ backgroundImage: "url('/assets/img/it/WebApplications.jpg')" }}></div>
                  </div>
                  <div className="col-xl-7 d-flex align-items-center">
                    <div className="card-body">
                      <h4 className="card-title">Web Applications</h4>
                      <p>Unlock the full potential of web applications with our development expertise. We specialize in creating custom web applications that streamline your business processes and engage your users. From project management tools to customer portals, we design and develop web apps that are tailored to your unique requirements, providing a seamless user experience.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* POS System */}
            <div className="col-lg-6" data-aos="fade-up" data-aos-delay="300">
              <div className="card-item">
                <div className="row">
                  <div className="col-xl-5">
                    <div className="card-bg" style={{ backgroundImage: "url('/assets/img/it/POSSystem.jpg')" }}></div>
                  </div>
                  <div className="col-xl-7 d-flex align-items-center">
                    <div className="card-body">
                      <h4 className="card-title">POS System</h4>
                      <p>Efficiently manage your point of sale operations with our comprehensive POS system solutions. Perfect Signs offers user-friendly and feature-rich POS systems tailored to your industry and needs. From inventory management to sales tracking, our POS systems are designed to streamline your business operations and enhance customer experiences.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Online Ordering Application */}
            <div className="col-lg-6" data-aos="fade-up" data-aos-delay="400">
              <div className="card-item">
                <div className="row">
                  <div className="col-xl-5">
                    <div className="card-bg" style={{ backgroundImage: "url('/assets/img/it/OnlineOrdering.jpg')" }}></div>
                  </div>
                  <div className="col-xl-7 d-flex align-items-center">
                    <div className="card-body">
                      <h4 className="card-title">Online Ordering Application</h4>
                      <p>Elevate your business with our online ordering applications. Whether you're in the restaurant, retail, or service industry, our solutions enable you to accept orders seamlessly online. Enhance customer convenience and increase revenue with user-friendly interfaces and secure payment processing.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="services-divider" data-aos="fade-up"></div>

          <div className="row gy-4">
            {/* SSL Certificate Setup */}
            <div className="col-lg-6" data-aos="fade-up" data-aos-delay="100">
              <div className="card-item">
                <div className="row">
                  <div className="col-xl-5">
                    <div className="card-bg" style={{ backgroundImage: "url('/assets/img/it/FreeSSL.jpg')" }}></div>
                  </div>
                  <div className="col-xl-7 d-flex align-items-center">
                    <div className="card-body">
                      <h4 className="card-title">SSL Certificate Setup</h4>
                      <p>Security is paramount online, which is why we provide free SSL certificates to safeguard your website and protect your customers' data. With our SSL certificates, you can assure your visitors that their information is encrypted, building trust and enhancing SEO rankings.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hosting Services */}
            <div className="col-lg-6" data-aos="fade-up" data-aos-delay="200">
              <div className="card-item">
                <div className="row">
                  <div className="col-xl-5">
                    <div className="card-bg" style={{ backgroundImage: "url('/assets/img/it/HostingServices.jpg')" }}></div>
                  </div>
                  <div className="col-xl-7 d-flex align-items-center">
                    <div className="card-body">
                      <h4 className="card-title">Hosting Services</h4>
                      <p>Experience top-tier hosting services with Perfect Signs. Our secure and reliable hosting solutions guarantee minimal downtime and fast loading speeds for your website. We offer various hosting options to accommodate your needs, whether you're a small business or a large enterprise. Rest easy knowing your online presence is in safe hands with our hosting expertise.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Database Design */}
            <div className="col-lg-6" data-aos="fade-up" data-aos-delay="300">
              <div className="card-item">
                <div className="row">
                  <div className="col-xl-5">
                    <div className="card-bg" style={{ backgroundImage: "url('/assets/img/it/DatabaseDesign.jpg')" }}></div>
                  </div>
                  <div className="col-xl-7 d-flex align-items-center">
                    <div className="card-body">
                      <h4 className="card-title">Database Design</h4>
                      <p>A robust database is the backbone of any dynamic web application. Our database designing services ensure your data is organized, secure, and easily accessible. We design databases that scale with your business and support the efficient storage and retrieval of information, ensuring your applications run smoothly.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Email Integrations */}
            <div className="col-lg-6" data-aos="fade-up" data-aos-delay="400">
              <div className="card-item">
                <div className="row">
                  <div className="col-xl-5">
                    <div className="card-bg" style={{ backgroundImage: "url('/assets/img/it/EmailIntegreation.jpg')" }}></div>
                  </div>
                  <div className="col-xl-7 d-flex align-items-center">
                    <div className="card-body">
                      <h4 className="card-title">Email Integrations</h4>
                      <p>Efficient communication is key to business success. We offer email integration services that seamlessly connect your website or applications to email platforms, ensuring that you can efficiently manage communication with customers, clients, and partners.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="services-divider" data-aos="fade-up"></div>

          <div className="row gy-4">
            {/* Chatbot */}
            <div className="col-lg-6" data-aos="fade-up" data-aos-delay="100">
              <div className="card-item">
                <div className="row">
                  <div className="col-xl-5">
                    <div className="card-bg" style={{ backgroundImage: "url('/assets/img/it/ChatBot.jpg')" }}></div>
                  </div>
                  <div className="col-xl-7 d-flex align-items-center">
                    <div className="card-body">
                      <h4 className="card-title">Chatbot</h4>
                      <p>Meet the future of customer support with our ChatBot solutions. Powered by AI, our ChatBots are available 24/7 to handle customer inquiries, provide assistance, and guide users through your website. They're more than just automated responses; they're intelligent virtual assistants that improve user experiences and reduce response times, ultimately enhancing customer satisfaction.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* SEO Optimization */}
            <div className="col-lg-6" data-aos="fade-up" data-aos-delay="200">
              <div className="card-item">
                <div className="row">
                  <div className="col-xl-5">
                    <div className="card-bg" style={{ backgroundImage: "url('/assets/img/it/SEO.jpg')" }}></div>
                  </div>
                  <div className="col-xl-7 d-flex align-items-center">
                    <div className="card-body">
                      <h4 className="card-title">SEO Optimization</h4>
                      <p>Achieving high visibility on search engines is crucial for attracting organic traffic. Our SEO optimization services are designed to boost your online presence. We employ the latest SEO techniques to optimize your website's content, structure, and performance. By improving your search engine rankings, we help you reach your target audience effectively and drive organic traffic that converts into customers.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>
      {/* End Services Cards Section */}

      {/* ======= IT CTA Section ======= */}
      <section className="it-cta">
        <div className="container" data-aos="fade-up">
          <div className="row align-items-center gy-4">
            <div className="col-lg-8" data-aos="fade-up" data-aos-delay="100">
              <p className="section-kicker">Build the next useful piece</p>
              <h2>Need a website, ordering flow, POS setup, or SEO cleanup?</h2>
              <p>Tell us where your business is getting stuck. We can help you choose a practical solution and avoid paying for technology you do not need.</p>
            </div>
            <div className="col-lg-4 text-lg-end" data-aos="fade-up" data-aos-delay="250">
              <Link to="/contact" className="it-cta-btn">Talk to Us</Link>
            </div>
          </div>
        </div>
      </section>
      {/* End IT CTA Section */}
    </>
  );
}
